export type Projection = {
  payload?: Record<string, any>; status?: string; projection_state?: string;
  source_revision?: number | string; domain_revisions?: Record<string, number | string>;
  stale?: boolean; background_updating?: boolean; last_known_good?: boolean;
  last_error?: string | null; [key: string]: unknown;
};
export function usableProjection(value: Projection | null | undefined): boolean {
  if (!value?.payload) return false;
  const state = String(value.payload.ui_projection?.state ?? value.projection_state ?? value.status ?? '').toUpperCase();
  return value.payload.projection_complete === true || state === 'READY';
}
function version(value: unknown): bigint | null {
  if ((typeof value !== 'number' && typeof value !== 'string') || !/^\d+$/.test(String(value))) return null;
  try { return BigInt(String(value)); } catch { return null; }
}
export function olderProjection(next: Projection, previous: Projection): boolean {
  const n = version(next.source_revision), p = version(previous.source_revision);
  if (n !== null && p !== null && n < p) return true;
  for (const [key, value] of Object.entries(previous.domain_revisions ?? {})) {
    const pv = version(value), nv = version(next.domain_revisions?.[key]);
    if (pv !== null && (nv === null || nv < pv)) return true;
  }
  return false;
}
/** One store per authenticated client; no sensitive projection persists to disk. */
export function createProjectionStore<T extends Projection>() {
  let actor: string | null = null, generation = 0;
  const cache = new Map<string, T>(), inflight = new Map<string, Promise<T | null>>();
  return {
    identify(next: string | null) { if (actor !== next) { actor = next; generation++; cache.clear(); inflight.clear(); } },
    getActor() { return actor; },
    get(screen: string) { return actor ? cache.get(screen) ?? null : null; },
    clear() { actor = null; generation++; cache.clear(); inflight.clear(); },
    async load(screen: string, reader: () => Promise<T | null>): Promise<T | null> {
      if (!actor) return null;
      const pending = inflight.get(screen); if (pending) return pending;
      const expectedActor = actor, epoch = generation;
      const request = (async () => {
        const next = await reader();
        if (actor !== expectedActor || generation !== epoch) return null;
        const previous = cache.get(screen);
        if (!next || !usableProjection(next)) return previous ? { ...previous, stale: true,
          background_updating: true, last_known_good: true, last_error: next?.last_error ?? null } : next;
        if (previous && olderProjection(next, previous)) return previous;
        cache.set(screen, next); return next;
      })();
      inflight.set(screen, request);
      try { return await request; } finally { if (inflight.get(screen) === request) inflight.delete(screen); }
    },
  };
}
